import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CogIcon,
  QueueListIcon,
  PencilIcon,
  AdjustmentsVerticalIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const StagingBanner: React.FC = () => {
  const getInitialCollapsedState = () => {
    const storedState = sessionStorage.getItem('stagingBannerCollapsed');
    return storedState ? JSON.parse(storedState) : true;
  };

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
  const location = useLocation();
  const params = useParams();

  const getBannerActions = useCallback(() => {
    const { pathname } = location;
    let actions: { label: string; to: string; target?: string; icon?: React.JSX.Element | string }[] = [];
    const adminLinks = [
      { label: 'Admin Dashboard', to: '/admin', icon: <CogIcon className="w-4 h-4" /> }, // Slightly larger icons
      { label: 'Vendors List', to: '/admin/vendors/', icon: <QueueListIcon className="w-4 h-4" /> },
    ];
    actions = [...adminLinks];
    if (pathname.startsWith('/vendor/') && params.vendorId) {
      actions.push({
        label: `Edit Vendor #${params.vendorId}`,
        to: `/admin/vendor/${params.vendorId}/edit`,
        icon: <PencilIcon className="w-4 h-4" />
      });
      actions.push({
        label: `Edit Menu #${params.vendorId}`,
        to: `/admin/vendor/${params.vendorId}/menu-edit`,
        icon: <AdjustmentsVerticalIcon className="w-4 h-4" />
      });
    }
    return actions;
  }, [location, params]);

  const bannerActions = getBannerActions();

  const getBannerHeight = useCallback(() => {
    if (isCollapsed) return 36; // Increased collapsed height for better touch
    return bannerActions.length > 0 ? 200 : 140; // Allow more height for expanded on mobile
  }, [isCollapsed, bannerActions]);

  useEffect(() => {
    if (window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('stagingBannerToggle', {
        detail: { isCollapsed, height: getBannerHeight(), type: 'cornerBanner' }
      }));
    }
  }, [isCollapsed, getBannerHeight]);

  useEffect(() => {
    sessionStorage.setItem('stagingBannerCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const ribbonBackgroundColor = 'rgba(200, 0, 0, 0.9)'; // Slightly more opaque
  const ribbonTextColor = '#FFFFFF';

  const expandedBackgroundColor = 'rgba(255, 255, 255, 0.98)';
  const expandedTextColor = '#1F2937';
  const expandedBorderColor = 'rgba(200, 0, 0, 0.7)';

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px', // Give a little space from the very edge
        right: '10px',
        width: isCollapsed ? 'auto' : 'min(280px, calc(100vw - 20px))', // Responsive width for expanded
        minHeight: isCollapsed ? '36px' : 'auto', // Ensure min height for tap
        backgroundColor: isCollapsed ? ribbonBackgroundColor : expandedBackgroundColor,
        color: isCollapsed ? ribbonTextColor : expandedTextColor,
        padding: isCollapsed ? '0' : '12px', // No padding for collapsed, handled by inner div
        borderRadius: '8px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
        zIndex: 99999,
        transition: 'all 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out',
        overflow: 'hidden',
        border: isCollapsed ? 'none' : `1px solid ${expandedBorderColor}`,
      }}
      role="status"
      aria-live="polite"
      data-height={getBannerHeight()} // This might be less relevant for layout now
    >
      {/* Clickable area for collapsed state / header for expanded state */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: isCollapsed ? '8px 12px' : '0 0 8px 0', // Padding inside for collapsed ribbon
          cursor: isCollapsed ? 'pointer' : 'default',
          borderBottom: isCollapsed ? 'none' : `1px solid rgba(0,0,0,0.08)`,
          marginBottom: isCollapsed ? '0' : '8px',
        }}
        onClick={isCollapsed ? toggleCollapse : undefined} // Make the whole ribbon clickable when collapsed
      >
        <div className="font-semibold flex items-center" style={{ fontSize: isCollapsed ? '12px' : '14px' }}>
          <ShieldExclamationIcon className={`w-4 h-4 mr-1.5 flex-shrink-0 ${isCollapsed ? 'text-yellow-300' : 'text-red-600'}`} />
          {isCollapsed ? 'STAGING' : 'STAGING ENVIRONMENT'}
        </div>
        <button
          onClick={(e) => {
            if (!isCollapsed) { // Prevent event bubbling if already expanded and clicking button
                e.stopPropagation();
            }
            toggleCollapse();
          }}
          className={`bg-transparent border-none cursor-pointer p-1 rounded-full 
                      ${isCollapsed ? 'text-gray-200 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          aria-label={isCollapsed ? "Expand staging banner" : "Collapse staging banner"}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ?
            <ChevronDownIcon className="w-5 h-5" /> : <ChevronUpIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Expanded Content (Actions) */}
      {!isCollapsed && (
        <div className="flex flex-col items-start gap-y-2 w-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}> {/* Max height relative to viewport */}
          {bannerActions.length > 0 ? bannerActions.map((action) => (
            <Link
              key={action.label}
              to={action.to}
              target={action.target || '_self'}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-md text-sm shadow-sm transition-colors duration-150 ease-in-out flex items-center w-full text-left" // Increased padding for tap targets
              style={{ border: '1px solid #e5e7eb' }}
            >
              {action.icon && <span className="mr-2 flex-shrink-0">{action.icon}</span>}
              <span className="truncate">{action.label}</span>
            </Link>
          )) : (
            <span className="text-xs italic text-gray-500 px-1 py-2">No specific actions for this page.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default StagingBanner;